/**
 * Preference Coordination Service
 * Centralized system to manage preference loading and prevent conflicts
 * between different initialization systems (theme hooks, language hooks, etc.)
 */

import { AppDispatch } from '../store';
import { LanguageCode } from '../store/slices/languageSlice';
import { ThemeMode, ColorTheme } from '../features/theme/types/core/themeTypes';
import { logger } from '../utils/logger';

export interface PreferenceState {
  userId: number;
  language?: LanguageCode;
  themeMode?: ThemeMode;
  colorTheme?: ColorTheme;
  timezone?: string;
  dateFormat?: 'relative' | 'absolute';
  emailNotifications?: boolean;
  pushNotifications?: boolean;
  extendedSettings?: Record<string, any>;
  isLoading: boolean;
  isApplied: boolean;
  loadedAt: number;
}

export interface PreferenceCoordinatorConfig {
  debounceMs?: number;
  maxRetries?: number;
  conflictResolutionStrategy?: 'latest' | 'server' | 'user';
}

/**
 * Centralized Preference Coordinator
 * Manages all preference loading and application to prevent conflicts
 */
export class PreferenceCoordinator {
  private userPreferences: Map<number, PreferenceState> = new Map();
  private loadingPromises: Map<number, Promise<PreferenceState>> = new Map();
  private applicationQueue: Array<{ userId: number; preferences: Partial<PreferenceState>; priority: number }> = [];
  private isProcessingQueue = false;
  // Event listeners for coordination
  private listeners: Map<string, Array<(event: any) => void>> = new Map();

  constructor(_config: PreferenceCoordinatorConfig = {}) {
  }

  /**
   * Register a system that wants to be notified of preference changes
   */
  registerSystem(systemName: string, callback: (event: any) => void): () => void {
    if (!this.listeners.has(systemName)) {
      this.listeners.set(systemName, []);
    }
    this.listeners.get(systemName)!.push(callback);

    // Return unregister function
    return () => {
      const callbacks = this.listeners.get(systemName);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Notify all registered systems of preference changes
   */
  private notifyListeners(event: { type: string; userId: number; preferences: PreferenceState }): void {
    this.listeners.forEach((callbacks, systemName) => {
      callbacks.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          logger.error(`PreferenceCoordinator: Error notifying ${systemName}`, error);
        }
      });
    });
  }

  /**
   * Load preferences for a user (main entry point)
   */
  async loadUserPreferences(
    userId: number,
    dispatch: AppDispatch,
    options: {
      loadLanguage?: boolean;
      loadTheme?: boolean;
      loadAllPreferences?: boolean;
      forceReload?: boolean;
    } = {}
  ): Promise<PreferenceState> {
    logger.service('PreferenceCoordinator', 'Loading preferences for user', { userId, options });

    // Check if already loading
    if (this.loadingPromises.has(userId)) {
      logger.service('PreferenceCoordinator', 'Already loading preferences for user', { userId });
      return this.loadingPromises.get(userId)!;
    }

    // Check if already loaded and not forcing reload
    const existingState = this.userPreferences.get(userId);
    if (existingState && existingState.isApplied && !options.forceReload) {
      logger.service('PreferenceCoordinator', 'Using existing preferences for user', { userId });
      return existingState;
    }

    // Start loading
    const loadingPromise = this.executePreferenceLoading(userId, dispatch, options);
    this.loadingPromises.set(userId, loadingPromise);

    try {
      const result = await loadingPromise;
      return result;
    } finally {
      this.loadingPromises.delete(userId);
    }
  }

  /**
   * Execute preference loading with proper coordination
   */
  private async executePreferenceLoading(
    userId: number,
    dispatch: AppDispatch,
    options: {
      loadLanguage?: boolean;
      loadTheme?: boolean;
      loadAllPreferences?: boolean;
    }
  ): Promise<PreferenceState> {
    // Initialize state
    const state: PreferenceState = {
      userId,
      isLoading: true,
      isApplied: false,
      loadedAt: Date.now()
    };

    this.userPreferences.set(userId, state);

    // Notify systems that loading is starting
    this.notifyListeners({
      type: 'preference_loading_start',
      userId,
      preferences: state
    });

    try {
      // Load preferences in parallel
      const loadingTasks: Promise<void>[] = [];

      if (options.loadLanguage !== false) {
        loadingTasks.push(this.loadLanguagePreference(userId, dispatch, state));
      }

      if (options.loadTheme !== false) {
        loadingTasks.push(this.loadThemePreference(userId, dispatch, state));
      }

      // Load all preferences from API if requested
      if (options.loadAllPreferences) {
        loadingTasks.push(this.loadAllPreferencesFromApi(userId, state));
      }

      // Wait for all loading to complete
      await Promise.allSettled(loadingTasks);

      // Mark as loaded but not yet applied
      state.isLoading = false;
      state.loadedAt = Date.now();

      logger.service('PreferenceCoordinator', 'Preferences loaded for user', { userId, state });

      // Queue for application (this ensures proper ordering)
      await this.queuePreferenceApplication(userId, state);

      return state;
    } catch (error) {
      logger.error('PreferenceCoordinator: Error loading preferences', error, { userId });
      state.isLoading = false;
      throw error;
    }
  }

  /**
   * Load language preference
   */
  private async loadLanguagePreference(userId: number, dispatch: AppDispatch, _state: PreferenceState): Promise<void> {
    try {
      logger.service('PreferenceCoordinator', 'Loading language for user', { userId });

      const { fetchUserLanguagePreference } = await import('../store/thunks/languageThunks');
      try {
        await fetchUserLanguagePreference(userId)(dispatch);
        logger.service('PreferenceCoordinator', 'Language loaded successfully', { userId });
        // Note: We can't easily get the actual language value here since it's applied directly to Redux
        // The language will be available in the Redux store after the thunk completes
      } catch (languageError) {
        logger.warn('PreferenceCoordinator: Language loading failed', { userId, error: languageError });
      }
    } catch (error) {
      logger.error('PreferenceCoordinator: Error loading language', error, { userId });
    }
  }

  /**
   * Load theme preference
   */
  private async loadThemePreference(userId: number, _dispatch: AppDispatch, state: PreferenceState): Promise<void> {
    try {
      logger.service('PreferenceCoordinator', 'Loading theme for user', { userId });

      const { themePreferenceService } = await import('./themePreferenceService');
      const themePreference = await themePreferenceService.loadThemeFromServer(userId);

      if (themePreference) {
        state.themeMode = themePreference.mode;
        state.colorTheme = themePreference.colorTheme;
        logger.service('PreferenceCoordinator', 'Theme loaded', { userId, mode: state.themeMode, color: state.colorTheme });
      } else {
        logger.service('PreferenceCoordinator', 'No theme preference found, using defaults', { userId });
        state.themeMode = 'system';
        state.colorTheme = 'purple';
      }
    } catch (error) {
      logger.error('PreferenceCoordinator: Error loading theme', error, { userId });
      // Set defaults on error
      state.themeMode = 'system';
      state.colorTheme = 'purple';
    }
  }

  /**
   * Load all preferences from API
   */
  private async loadAllPreferencesFromApi(userId: number, state: PreferenceState): Promise<void> {
    try {
      logger.service('PreferenceCoordinator', 'Loading all preferences from API', { userId });

      const { userPreferencesApi } = await import('./api/userPreferencesApi');
      const preferences = await userPreferencesApi.getUserPreferences(userId);

      // Update state with all preference fields
      state.timezone = preferences.timezone;
      state.dateFormat = preferences.dateFormat;
      state.emailNotifications = preferences.emailNotifications;
      state.pushNotifications = preferences.pushNotifications;
      state.extendedSettings = preferences.extendedSettings;

      logger.service('PreferenceCoordinator', 'All preferences loaded from API', { userId, preferences });
    } catch (error) {
      logger.error('PreferenceCoordinator: Error loading all preferences from API', error, { userId });
      // Set defaults on error
      state.timezone = 'UTC';
      state.dateFormat = 'relative';
      state.emailNotifications = true;
      state.pushNotifications = true;
    }
  }

  /**
   * Queue preference application to ensure proper ordering
   */
  private async queuePreferenceApplication(userId: number, state: PreferenceState): Promise<void> {
    return new Promise((resolve) => {
      // Add to queue with high priority (lower number = higher priority)
      this.applicationQueue.push({
        userId,
        preferences: state,
        priority: Date.now() // Use timestamp for FIFO ordering
      });

      // Sort queue by priority
      this.applicationQueue.sort((a, b) => a.priority - b.priority);

      // Process queue
      this.processApplicationQueue().then(() => resolve());
    });
  }

  /**
   * Process the application queue
   */
  private async processApplicationQueue(): Promise<void> {
    if (this.isProcessingQueue || this.applicationQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    try {
      while (this.applicationQueue.length > 0) {
        const item = this.applicationQueue.shift()!;
        await this.applyPreferences(item.userId, item.preferences as PreferenceState);
        
        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Apply preferences to the system
   */
  private async applyPreferences(userId: number, state: PreferenceState): Promise<void> {
    try {
      logger.service('PreferenceCoordinator', 'Applying preferences for user', { userId, state });

      // Notify systems that application is starting
      this.notifyListeners({
        type: 'preference_application_start',
        userId,
        preferences: state
      });

      // Apply language
      if (state.language) {
        await this.applyLanguagePreference(state.language);
      }

      // Apply theme
      if (state.themeMode && state.colorTheme) {
        await this.applyThemePreference(state.themeMode, state.colorTheme);
      }

      // Mark as applied
      state.isApplied = true;
      this.userPreferences.set(userId, state);

      // Notify systems that application is complete
      this.notifyListeners({
        type: 'preference_application_complete',
        userId,
        preferences: state
      });

      logger.service('PreferenceCoordinator', 'Preferences applied successfully for user', { userId });
    } catch (error) {
      logger.error('PreferenceCoordinator: Error applying preferences', error, { userId });
      
      // Notify systems of error
      this.notifyListeners({
        type: 'preference_application_error',
        userId,
        preferences: state
      });
    }
  }

  /**
   * Apply language preference
   */
  private async applyLanguagePreference(language: LanguageCode): Promise<void> {
    try {
      // Import Redux actions
      const { setLanguage } = await import('../store/slices/languageSlice');
      const { updateHtmlAttributes } = await import('../features/language/utils/languageUtils');
      
      // Get dispatch from the store
      const storeModule = await import('../store');
      const store = storeModule.default;
      store.dispatch(setLanguage(language));
      
      // Update HTML attributes
      updateHtmlAttributes(language);
      
      logger.service('PreferenceCoordinator', 'Language applied', { language });
    } catch (error) {
      logger.error('PreferenceCoordinator: Error applying language', error, { language });
    }
  }

  /**
   * Apply theme preference
   */
  private async applyThemePreference(mode: ThemeMode, colorTheme: ColorTheme): Promise<void> {
    try {
      // Import Redux actions and utilities
      const { setThemeMode, setColorTheme } = await import('../store/slices/themeSlice');
      const { applyThemeToDocument } = await import('../features/theme/utils/simpleThemeUtils');
      
      // Get dispatch from the store
      const storeModule = await import('../store');
      const store = storeModule.default;
      store.dispatch(setThemeMode(mode));
      store.dispatch(setColorTheme(colorTheme));
      
      // Apply to document
      applyThemeToDocument(mode, colorTheme);
      
      logger.service('PreferenceCoordinator', 'Theme applied', { mode, colorTheme });
    } catch (error) {
      logger.error('PreferenceCoordinator: Error applying theme', error, { mode, colorTheme });
    }
  }

  /**
   * Get current preferences for a user
   */
  getUserPreferences(userId: number): PreferenceState | null {
    return this.userPreferences.get(userId) || null;
  }

  /**
   * Check if preferences are currently loading for a user
   */
  isLoadingPreferences(userId: number): boolean {
    return this.loadingPromises.has(userId);
  }

  /**
   * Clear preferences for a user (useful for logout)
   */
  clearUserPreferences(userId: number): void {
    this.userPreferences.delete(userId);
    this.loadingPromises.delete(userId);
    
    // Remove from queue
    this.applicationQueue = this.applicationQueue.filter(item => item.userId !== userId);
    
    logger.cleanup('Cleared preferences for user', { userId });
  }

  /**
   * Clear all preferences (useful for cleanup)
   */
  clearAllPreferences(): void {
    this.userPreferences.clear();
    this.loadingPromises.clear();
    this.applicationQueue = [];
    this.listeners.clear();
    
    logger.cleanup('Cleared all preferences');
  }

  /**
   * Get coordination status (for debugging)
   */
  getStatus(): {
    loadedUsers: number[];
    loadingUsers: number[];
    queueLength: number;
    isProcessingQueue: boolean;
  } {
    return {
      loadedUsers: Array.from(this.userPreferences.keys()),
      loadingUsers: Array.from(this.loadingPromises.keys()),
      queueLength: this.applicationQueue.length,
      isProcessingQueue: this.isProcessingQueue
    };
  }
}

// Export singleton instance
export const preferenceCoordinator = new PreferenceCoordinator();

// Export types (remove duplicate exports)
export type { PreferenceState as PreferenceCoordinatorState, PreferenceCoordinatorConfig as PreferenceConfig };